import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Activity,
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  Eye,
  FileSearch,
  Link2,
  Mail,
  Network,
  PanelRightOpen,
  Paperclip,
  PencilLine,
  ShieldCheck,
  ShieldQuestion,
  UserRound,
  X,
} from 'lucide-react'
import clsx from 'clsx'
import type { Loan } from '@/types'
import { loansService } from '@/services/loans'
import { ingestionService, type SourceFileRef } from '@/services/ingestion'
import SourceViewerDialog from '@/components/intake/SourceViewerDialog'
import DataReviewPanel from '@/components/review/DataReviewPanel'
import { ConfirmationViewDialog } from '@/components/intake/ConfirmationView'
import { PendingMark } from '@/components/review/PendingMark'
import { useFieldConfirmation } from '@/components/review/useFieldConfirmation'
import { LOAN_QUERY_KEYS, invalidateLoanQueries } from '@/services/loanQueryKeys'
import { KillDealPanel } from '@/components/deal/KillDealPanel'
import { useDealDossierStore } from '@/store/dealDossierStore'
import { FieldStateBadge } from '@/components/fields/fieldState'
import { heldValue, stateOfLineage } from '@/components/fields/lineage'
import type {
  EditableLoanPatch,
  FieldValueState,
  InspectorLogKind,
  InspectorTab,
  LoanFieldHistoryEvent,
  LoanFieldLineageField,
} from './types'
import { totalAgeDays } from '@/utils/dealAge'
import { commitmentBasis, commitmentSplit, completeness, completenessBasis, counterparty, counterpartyText, criticalMissing, derivedBasis, loanExceptions, money, moneyRecorded, requestedBasis, requestedCommitment } from './helpers'
import { DerivedTag } from './DerivedTag'
import { debtYieldPct, debtYieldText, loanPropertyValue } from './loanFigures'
import { ltvWithImplied } from '@/components/deal/dossier/valueBasis'
import { httpStatus, loanSaveMessage } from '@/components/deal/dossier/httpStatus'
import { loanAmountError } from '@/services/dealFile'
import { useOptionalDossierEdit } from '@/components/deal/dossier/editContext'
import { NO_DEAL_EDIT, useCanEditDeal } from '@/components/deal/dossier/dealEditPermission'
import { WORKSPACE_TO_SNAKE } from '@/components/deal/dossier/editableFields'
import { ActivitySummaryTab, BlockersTab, EconomicsTab } from './InspectorSummaryTabs'
import { InspectorLogActionRow, InspectorLogCompose } from './InspectorLogActions'
import { useReviewGap } from './ReviewGapMarker'
import { currencyOf, moneyWithCode } from './currency'
import { requiredAtIntakeMissing } from './intakeRequired'
import { ResponsiblePartySelect } from './ResponsiblePartySelect'

// Keys and the invalidation set live in services/loanQueryKeys so the deal-file
// dialog invalidates exactly what this panel reads. Two private copies would
// drift, and the symptom — edit here, stale value there — is invisible in tests.
const invalidateLoanInspectorQueries = invalidateLoanQueries

/** Normalize client vs API field identifiers for badge matching only. */
function fieldKeyAliases(name: string): string[] {
  const raw = name.trim()
  const snake = raw
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/\s+/g, '_')
    .toLowerCase()
  const aliases = new Set([raw, raw.toLowerCase(), snake])
  const remap: Record<string, string[]> = {
    property: ['property_name'],
    property_name: ['property'],
    type: ['asset_type'],
    asset_type: ['type'],
    loanamount: ['loan_amount'],
    loan_amount: ['loanAmount', 'loanamount'],
    brokername: ['broker_name'],
    broker_name: ['brokerName', 'brokername'],
    fundedamount: ['funded_amount'],
    funded_amount: ['fundedAmount', 'fundedamount'],
  }
  for (const extra of remap[snake] ?? []) aliases.add(extra)
  return [...aliases]
}

/** The Data Intelligence Review datapoint behind a "Missing field" this panel
 *  names. A field with no entry (the owner, the received date, the funded
 *  amount) is not something the engine reads from a package, so it has no
 *  review to open. */
const REVIEW_FIELD: Record<string, string> = {
  property: 'deal_name',
  sponsor: 'sponsor',
  location: 'location',
  type: 'asset_type',
  loanAmount: 'loan_amount',
  purpose: 'purpose',
  ltv: 'ltv',
  dscr: 'dscr',
}

function formatFieldValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }
  return String(value)
}

/** The shared field-state badge — the four agreed value states and the
 *  absences — used everywhere a field renders. */
function ValueStateBadge({ state }: { state: FieldValueState | string }) {
  return <FieldStateBadge state={state} />
}

function SectionLabel({ label, action }: { label: string; action?: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-500">{label}</div>
      {action ? <div className="text-[10px] text-amber-200/80">{action}</div> : null}
    </div>
  )
}

function InfoRow({
  icon: Icon,
  label,
  value,
  suggested,
  pending,
  derived,
}: {
  icon: typeof Building2
  label: string
  value: string
  /** Basis of a Suggested value — drawn as the grid's 'suggested' chip. */
  suggested?: string
  /** Basis of a figure the platform computed — drawn as the grid's 'calc' mark. */
  derived?: string | null
  /** Set (possibly to '') when no person has confirmed this value yet — drawn
   *  as the amber 'pending' mark; the string is its basis on hover. */
  pending?: string | null
}) {
  return (
    <div className="flex items-start gap-2.5 rounded-md border border-white/[0.04] bg-white/[0.015] px-2.5 py-2">
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-200/70" />
      <div className="min-w-0">
        <div className="text-[9px] uppercase tracking-[0.14em] text-stone-600">{label}</div>
        <div className="mt-0.5 flex min-w-0 items-center text-[12px] text-stone-200">
          <span className="truncate">{value}</span>
          <DerivedTag basis={derived ?? null} />
          {suggested ? (
            <span
              className="ml-1 shrink-0 rounded border border-dashed border-stone-500/50 px-1 text-[9px] text-stone-400"
              title={suggested}
            >
              suggested
            </span>
          ) : null}
          {pending !== null && pending !== undefined ? <PendingMark basis={pending} /> : null}
        </div>
      </div>
    </div>
  )
}

/** Percent-scaled slots read and write a percentage; everything else carries
 *  the number the wire carries. */
export type InputScale = 'plain' | 'percent'

export function toInput(value: unknown, scale: InputScale): string {
  if (value === null || value === undefined || value === '') return ''
  const n = Number(value)
  if (Number.isNaN(n)) return ''
  // 0.6239 -> "62.39", rounded so 0.0725 does not surface as 7.249999999999999.
  return scale === 'percent' ? String(Math.round(n * 1_000_000) / 10_000) : String(n)
}

/** A native percent the save would refuse, said before it is sent. 'LTV % =
 *  200' became 2.0 here, the API read anything above 1.5 as points and
 *  divided again, and the deal saved a 2% LTV with 'Deal updated' (values-02). */
export function percentFieldError(label: string, fraction: unknown): string | null {
  if (typeof fraction !== 'number' || !Number.isFinite(fraction)) return null
  if (fraction >= 0 && fraction <= 1) return null
  return `${label} must be between 0% and 100% (entered ${Math.round(fraction * 10_000) / 100}%). Nothing was saved.`
}

export function fromInput(raw: string, scale: InputScale): number | undefined {
  if (raw === '') return undefined
  const n = Number(raw)
  if (Number.isNaN(n)) return undefined
  return scale === 'percent' ? n / 100 : n
}

/**
 * The part of the draft that differs from what the deal already holds.
 *
 * `startEdit` seeds the draft with every editable field so the inputs open
 * populated. Posting that whole draft meant one edit re-asserted nine other
 * values, so the write was only as valid as its weakest field — a deal whose
 * property name was blank refused the assignee change someone had just typed,
 * and said nothing about why. It also wrote nine no-op assertions into the
 * deal's audit trail on every single edit.
 */
export function changedFields(draft: EditableLoanPatch, loan: Loan): EditableLoanPatch {
  const patch: EditableLoanPatch = {}
  for (const [key, next] of Object.entries(draft)) {
    const current = (loan as unknown as Record<string, unknown>)[key]
    // '' and null both mean "nothing here" once a text input has been cleared.
    const bothEmpty =
      (next === '' || next === null || next === undefined) &&
      (current === '' || current === null || current === undefined)
    if (bothEmpty || next === current) continue
    ;(patch as Record<string, unknown>)[key] = next
  }
  return patch
}

export function OverviewTab({
  loan,
  exceptions,
  onSave,
  saving,
}: {
  loan: Loan
  exceptions: string[]
  onSave: (patch: EditableLoanPatch) => Promise<void>
  saving: boolean
}) {
  const queryClient = useQueryClient()
  // The critical gaps, and the loan purpose a deal can lack though no pipeline
  // cell is waiting on it (required at intake — client item #1).
  const missing = [...criticalMissing(loan), ...requiredAtIntakeMissing(loan)]
  const dossier = useOptionalDossierEdit()
  const followHeader = Boolean(dossier)
  // The Save below is PUT /loans/{id}: pipeline:edit. A role without it is not
  // offered the form, here or by following an unlocked dossier header.
  const canEditDeal = useCanEditDeal()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<EditableLoanPatch>({})
  const [saveError, setSaveError] = useState<string | null>(null)
  // The Data Intelligence Review, opened on this deal — at a field, or at the top.
  const [review, setReview] = useState<{ field?: string } | null>(null)
  // The Confirmation view: every field, populated and missing, and who has
  // confirmed it. Reads the lineage this tab already fetches.
  const [confirmOpen, setConfirmOpen] = useState(false)
  const confirmation = useFieldConfirmation(loan.id)
  // A contested sponsor is blank on the loan with rivals in Smart Review. The
  // line names nobody then: the borrowing entity read as the answer (other-11).
  const sponsorGap = useReviewGap(loan.id, 'sponsor')
  const rivals = sponsorGap?.candidates ?? 0
  const sponsorLine =
    sponsorGap?.status === 'conflict' && !counterparty(loan)
      ? `— · ${rivals > 0 ? `${rivals} rival${rivals === 1 ? '' : 's'}` : 'in conflict'}`
      : counterpartyText(loan) || 'Missing'

  const lineageQuery = useQuery({
    queryKey: LOAN_QUERY_KEYS.fieldLineage(loan.id),
    queryFn: () => loansService.getFieldLineage(loan.id),
  })

  const missingStateByLabel = useMemo(() => {
    const map = new Map<string, string>()
    for (const field of lineageQuery.data?.fields ?? []) {
      if (field.valueState !== 'missing') continue
      for (const key of fieldKeyAliases(field.fieldName)) {
        map.set(key, field.valueState)
      }
    }
    return map
  }, [lineageQuery.data?.fields])

  useEffect(() => {
    setEditing(false)
    setDraft({})
    setReview(null)
    setConfirmOpen(false)
  }, [loan.id])

  useEffect(() => {
    if (!followHeader) return
    if (dossier?.unlocked && canEditDeal) {
      setDraft({
        property: loan.property,
        sponsor: counterparty(loan),
        location: loan.location,
        assignee: loan.assignee,
        originated: loan.originated,
        ltv: loan.ltv,
        dscr: loan.dscr,
        loanAmount: loan.loanAmount,
        rate: loan.rate,
        brokerName: loan.brokerName ?? '',
      })
      setEditing(true)
      return
    }
    setEditing(false)
  }, [
    followHeader,
    dossier?.unlocked,
    canEditDeal,
    loan.id,
    loan.property,
    loan.borrower,
    loan.sponsor,
    loan.location,
    loan.assignee,
    loan.originated,
    loan.ltv,
    loan.dscr,
    loan.loanAmount,
    loan.rate,
    loan.brokerName,
  ])

  const startEdit = () => {
    if (!canEditDeal) return
    setDraft({
      property: loan.property,
      sponsor: counterparty(loan),
      location: loan.location,
      assignee: loan.assignee,
      originated: loan.originated,
      ltv: loan.ltv,
      dscr: loan.dscr,
      loanAmount: loan.loanAmount,
      rate: loan.rate,
      brokerName: loan.brokerName ?? '',
    })
    setEditing(true)
  }

  // After a 409 the row is reloaded (see the save handler). The draft was
  // seeded with every field of the OLD row, so a retry would have sent the
  // old values of the fields the other editor changed and quietly undone
  // them. Once the reloaded row arrives, the form takes its values and keeps
  // only what this person changed. Adjusted during render (not in an effect),
  // so no render can offer a Save over the old draft.
  const [reapply, setReapply] = useState<{ patch: EditableLoanPatch; fromVersion: string | null } | null>(null)
  const rowVersion = loan.updatedAt ?? loan.createdAt ?? null
  if (reapply && rowVersion !== reapply.fromVersion) {
    setDraft({
      property: loan.property,
      sponsor: counterparty(loan),
      location: loan.location,
      assignee: loan.assignee,
      originated: loan.originated,
      ltv: loan.ltv,
      dscr: loan.dscr,
      loanAmount: loan.loanAmount,
      rate: loan.rate,
      brokerName: loan.brokerName ?? '',
      ...reapply.patch,
    })
    setReapply(null)
  }

  return (
    <div className="space-y-5">
      <section>
        {/* Register B2 — plain language: this is the share of core fields
            filled in, not a readiness verdict. */}
        <SectionLabel
          label="Decision snapshot"
          action={
            completeness(loan) == null
              ? 'Completeness not measured'
              : `${completeness(loan)}% complete · ${completenessBasis(loan) ?? 'catalog fields'}`
          }
        />
        <div className="mt-2 grid grid-cols-2 gap-2">
          <InfoRow
            icon={Building2}
            label="Property"
            value={loan.property || 'Missing'}
            pending={confirmation.pendingBasis('property_name')}
          />
          {/* The borrowing entity copied over because no document names a
              sponsor is a Suggestion — marked as the grid row marks it. */}
          <InfoRow
            icon={UserRound}
            label="Sponsor / Borrower"
            value={sponsorLine}
            suggested={
              loan.sponsorSuggested && counterparty(loan)
                ? 'Suggested — copied from the borrowing entity; no document names a sponsor.'
                : undefined
            }
            pending={counterparty(loan) ? confirmation.pendingBasis('sponsor') : null}
          />
          <InfoRow icon={ShieldCheck} label="Stage" value={loan.stage} />
          {/* `loans.loan_amount` is NOT NULL: 0 records that no amount was
              stated (a pitch with no ask), never a $0 request. Requested is
              the grid's basis — the total commitment where the split is
              recorded — with the initial advance beside it (OVR-19); the raw
              loan amount holds Calloway's advance, not its commitment. */}
          {/* The deal's money is held in its own currency and never converted,
              so each figure carries its ISO code. */}
          <InfoRow
            icon={Activity}
            label="Requested"
            value={moneyWithCode(moneyRecorded(requestedCommitment(loan)), currencyOf(loan))}
            derived={requestedCommitment(loan) == null ? null : requestedBasis(loan)}
            pending={confirmation.pendingBasis('loan_amount')}
          />
          <InfoRow
            icon={Link2}
            label="Initial advance"
            value={moneyWithCode(money(commitmentSplit(loan).initial), currencyOf(loan))}
            derived={commitmentSplit(loan).initial == null ? null : commitmentBasis(loan).initial}
          />
          <InfoRow
            icon={FileSearch}
            label="Broker"
            value={loan.brokerName || 'Not linked'}
            pending={loan.brokerName ? confirmation.pendingBasis('broker_name') : null}
          />
        </div>
        {/* Ingested values are usable on arrival and stay pending until a
            person confirms them — who and when are recorded per field. */}
        <div className="mt-2 flex items-center justify-between gap-2 rounded-md border border-white/[0.04] bg-white/[0.015] px-2.5 py-2">
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-[0.14em] text-stone-600">Confirmation</div>
            <div className="mt-0.5 truncate text-[12px] text-stone-300" data-testid="confirmation-counts">
              {confirmation.isLoading
                ? 'Reading…'
                : `${confirmation.counts.confirmed} confirmed · ${confirmation.counts.pending} pending`}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            className="inline-flex shrink-0 items-center gap-1 rounded border border-amber-300/30 px-2 py-1 text-[10px] text-amber-200 hover:bg-amber-300/10"
          >
            <ClipboardCheck className="h-3 w-3" /> Review &amp; confirm
          </button>
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between gap-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-500">
            Missing / exceptions
          </div>
          {missing.length || exceptions.length ? (
            <button
              type="button"
              onClick={() => setReview({})}
              className="inline-flex items-center gap-1 rounded border border-amber-300/30 px-2 py-0.5 text-[10px] text-amber-200 hover:bg-amber-300/10"
            >
              <ShieldQuestion className="h-3 w-3" /> Needs attention — review
            </button>
          ) : (
            <div className="text-[10px] text-amber-200/80">Clear</div>
          )}
        </div>
        <div className="mt-2 space-y-1.5">
          {missing.length === 0 && exceptions.length === 0 ? (
            <div className="rounded-md border border-emerald-400/15 bg-emerald-400/[0.05] px-3 py-2 text-[12px] text-emerald-200">
              No critical gaps or exceptions on this record.
            </div>
          ) : (
            <>
              {missing.map((field) => {
                const state = fieldKeyAliases(field)
                  .map((key) => missingStateByLabel.get(key))
                  .find(Boolean)
                return (
                  <div
                    key={field}
                    className="flex items-center justify-between gap-2 rounded-md border border-amber-300/15 bg-amber-300/[0.05] px-3 py-2 text-[12px] text-amber-100"
                  >
                    <span>Missing field: {field}</span>
                    <span className="flex items-center gap-2">
                      {state ? <ValueStateBadge state={state} /> : null}
                      {REVIEW_FIELD[field] ? (
                        <button
                          type="button"
                          onClick={() => setReview({ field: REVIEW_FIELD[field] })}
                          aria-label={`Review ${field}`}
                          className="rounded border border-amber-300/30 px-1.5 py-0.5 text-[10px] text-amber-200 hover:bg-amber-300/10"
                        >
                          Review
                        </button>
                      ) : null}
                    </span>
                  </div>
                )
              })}
              {exceptions.filter((item) => item !== 'Critical data missing').map((item) => (
                <div key={item} className="rounded-md border border-red-400/15 bg-red-400/[0.05] px-3 py-2 text-[12px] text-red-200">
                  {item}
                </div>
              ))}
              {(loan.creditBoxExceptions ?? []).length > 0 && loan.creditBoxRulesNote ? (
                <div className="px-1 text-[11px] text-stone-500">
                  Credit box thresholds: {loan.creditBoxRulesNote}
                </div>
              ) : null}
            </>
          )}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between gap-2">
          <SectionLabel label="Live edit" />
          {!followHeader && !editing ? (
            <button
              type="button"
              onClick={startEdit}
              disabled={!canEditDeal}
              title={canEditDeal ? undefined : NO_DEAL_EDIT}
              className="inline-flex items-center gap-1 rounded border border-amber-300/20 px-2 py-1 text-[10px] text-amber-200 hover:bg-amber-300/10 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <PencilLine className="h-3 w-3" /> Edit fields
            </button>
          ) : null}
          {followHeader && !(dossier?.unlocked && canEditDeal) ? (
            <span className="text-[10px] text-stone-500">
              {canEditDeal ? 'Unlock to edit in the header' : NO_DEAL_EDIT}
            </span>
          ) : null}
        </div>
        {editing && canEditDeal ? (
          <form
            className="mt-2 space-y-2"
            onSubmit={async (event) => {
              event.preventDefault()
              setSaveError(null)
              // Only what actually changed. The draft is seeded with every
              // field, so posting it whole made one edit carry nine other
              // values along — and any one of them the server disliked sank
              // the edit the person actually made.
              const patch = changedFields(draft, loan)
              if (Object.keys(patch).length === 0) {
                if (!followHeader) setEditing(false)
                return
              }
              // Amounts are dollars; "45" meant $45M and was stored as $45.
              const amountError =
                typeof patch.loanAmount === 'number' ? loanAmountError('Amount', patch.loanAmount) : null
              if (amountError) {
                setSaveError(amountError)
                return
              }
              const ltvError = percentFieldError('LTV', patch.ltv)
              if (ltvError) {
                setSaveError(ltvError)
                return
              }
              try {
                await onSave(patch)
              } catch (err) {
                // A rejected save used to end here in silence: the button went
                // back to "Save changes" and nothing on the page said why.
                if (httpStatus(err) === 409) {
                  // Refused as out of date (CMP-04). The save names the version
                  // of the row it was given, so until the row is reloaded every
                  // retry sent the same stale version and was refused again.
                  // Reload it: the other editor's change shows, and the next
                  // save names the current version.
                  if (!followHeader) setReapply({ patch, fromVersion: rowVersion })
                  await invalidateLoanInspectorQueries(queryClient, loan.id)
                  setSaveError(
                    `${loanSaveMessage(err)} The deal has been reloaded with their change` +
                      (followHeader
                        ? '; re-apply yours.'
                        : ' — your edits are still in the form; Save again to apply them over it.'),
                  )
                  return
                }
                setSaveError(loanSaveMessage(err))
                return
              }
              await invalidateLoanInspectorQueries(queryClient, loan.id)
              if (!followHeader) setEditing(false)
            }}
          >
            {(
              [
                ['property', 'Property'],
                // The sponsor. The borrowing entity is its own field and is
                // never seeded here: an edit of any other field wrote a
                // contested deal's SPV into its sponsor (other-11).
                ['sponsor', 'Sponsor / Borrower'],
                ['location', 'Location'],
                // An internal underwriter or user, picked from a list.
                ['assignee', 'Responsible Party'],
                // The catalogue's "Date Received": when the package arrived.
                ['originated', 'Received'],
                ['brokerName', 'Broker'],
                ['rate', 'Rate'],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="block">
                <span className="text-[9px] uppercase tracking-[0.14em] text-stone-600">{label}</span>
                {key === 'assignee' ? (
                  <ResponsiblePartySelect
                    value={String(draft[key] ?? '')}
                    onChange={(value) => {
                      setSaveError(null)
                      setDraft((current) => ({ ...current, [key]: value }))
                      dossier?.setField(WORKSPACE_TO_SNAKE[key] ?? key, value)
                    }}
                    className="mt-1 w-full rounded border border-stone-700 bg-black/40 px-2 py-1.5 text-[12px] text-stone-100 outline-none focus:border-amber-300/40"
                  />
                ) : (
                  <input
                    value={String(draft[key] ?? '')}
                    onChange={(e) => {
                      const value = e.target.value
                      setSaveError(null)
                      setDraft((current) => ({ ...current, [key]: value }))
                      dossier?.setField(WORKSPACE_TO_SNAKE[key] ?? key, value)
                    }}
                    className="mt-1 w-full rounded border border-stone-700 bg-black/40 px-2 py-1.5 text-[12px] text-stone-100 outline-none focus:border-amber-300/40"
                  />
                )}
                {key === 'location' ? (
                  <span className="mt-0.5 block text-[9px] leading-snug text-stone-600">
                    The street, city, state and ZIP this line states replace the deal&apos;s address
                    everywhere — the dossier, the map and the printed report.
                  </span>
                ) : null}
              </label>
            ))}
            <div className="grid grid-cols-3 gap-2">
              {(
                [
                  ['loanAmount', 'Amount', 'plain'],
                  // LTV rides the wire as a fraction, and `pct()` renders it
                  // times 100 everywhere else on this screen. Bound raw, the
                  // box read "0.62" beside a grid column reading "62.0%", and
                  // anyone typing the number they could see stored 6200%.
                  ['ltv', 'LTV %', 'percent'],
                  ['dscr', 'DSCR', 'plain'],
                ] as const
              ).map(([key, label, scale]) => (
                <label key={key} className="block">
                  <span className="text-[9px] uppercase tracking-[0.14em] text-stone-600">{label}</span>
                  <input
                    type="number"
                    step="any"
                    value={toInput(draft[key], scale)}
                    onChange={(e) => {
                      const next = fromInput(e.target.value, scale)
                      setSaveError(null)
                      setDraft((current) => ({ ...current, [key]: next }))
                      dossier?.setField(WORKSPACE_TO_SNAKE[key] ?? key, next)
                    }}
                    className="mt-1 w-full rounded border border-stone-700 bg-black/40 px-2 py-1.5 text-[12px] text-stone-100 outline-none focus:border-amber-300/40"
                  />
                </label>
              ))}
            </div>
            {saveError ? (
              <p
                role="alert"
                className="rounded border border-red-400/25 bg-red-400/[0.07] px-2.5 py-2 text-[11px] text-red-200"
              >
                {saveError}
              </p>
            ) : null}
            <div className="flex gap-2 pt-1">
              <button
                type="submit"
                disabled={saving}
                className="rounded bg-amber-300 px-3 py-1.5 text-[11px] font-medium text-black disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
              {!followHeader && (
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="rounded border border-stone-600 px-3 py-1.5 text-[11px] text-stone-300"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        ) : (
          <div className="mt-2 grid grid-cols-2 gap-2 text-[12px] text-stone-300">
            <div>
              {/* The Economics tab's and the dossier's LTV: the stated
                  figure, with the implied one beside it when they differ. */}
              LTV {ltvWithImplied(loan.ltv, loan.loanAmount, loanPropertyValue(loan))}
              <DerivedTag basis={loan.ltv > 0 ? derivedBasis(loan, 'ltv') : null} />
            </div>
            <div>
              DSCR {loan.dscr > 0 ? `${loan.dscr.toFixed(2)}x` : '—'}
              <DerivedTag basis={loan.dscr > 0 ? derivedBasis(loan, 'dscr') : null} />
            </div>
            <div>
              Debt yield {debtYieldText(loan, debtYieldPct)}
              <DerivedTag basis={derivedBasis(loan, 'debt_yield')} />
            </div>
            {/* E5: dwell in the current stage leads; cumulative age follows it
                so the two are never mistaken for each other. */}
            <div>
              In stage{' '}
              {loan.daysInStatus === null || loan.daysInStatus === undefined
                ? '—'
                : `${loan.daysInStatus}d`}
            </div>
            <div title="Counted from when the record entered the system">
              Total age {totalAgeDays(loan) == null ? '—' : `${totalAgeDays(loan)}d since entry`}
            </div>
            <div>Responsible party {loan.assignee || '—'}</div>
            <div>Received {loan.originated || '—'}</div>
          </div>
        )}
      </section>

      {review ? (
        <DataReviewPanel loanId={loan.id} initialField={review.field} onClose={() => setReview(null)} />
      ) : null}
      {confirmOpen ? <ConfirmationViewDialog loanId={loan.id} onClose={() => setConfirmOpen(false)} /> : null}
    </div>
  )
}

/** The Economics tab with the broker-stated figures beside the platform's own
 *  (Gate 0 D1). Reads the field lineage the other tabs already fetch, under the
 *  same key, so it costs no second request. */
function InspectorEconomicsTab({ loan }: { loan: Loan }) {
  const lineage = useQuery({
    queryKey: LOAN_QUERY_KEYS.fieldLineage(loan.id),
    queryFn: () => loansService.getFieldLineage(loan.id),
  })
  return <EconomicsTab loan={loan} statedFigures={lineage.data?.statedFigures} />
}

function InspectorConnections({ loan, allLoans }: { loan: Loan; allLoans: Loan[] }) {
  const related = useMemo(
    () =>
      allLoans.filter(
        (candidate) =>
          candidate.id !== loan.id &&
          ((loan.sponsor && candidate.sponsor === loan.sponsor) ||
            (loan.borrower && candidate.borrower === loan.borrower)),
      ).slice(0, 6),
    [allLoans, loan],
  )
  const [viewerSha, setViewerSha] = useState<string | null>(null)
  const sourcePkg = useQuery({
    queryKey: ['ingestion-source', loan.sourceIngestionId],
    queryFn: () => ingestionService.getById(loan.sourceIngestionId!),
    enabled: Boolean(loan.sourceIngestionId),
    staleTime: 60_000,
  })
  const sourceFiles: SourceFileRef[] = sourcePkg.data?.rawFilePaths ?? []

  return (
    <div className="space-y-4">
      <SectionLabel label="Linked entities" />
      <div className="grid gap-2">
        <InfoRow icon={UserRound} label="Broker" value={loan.brokerName || 'Not linked'} />
        <InfoRow
          icon={FileSearch}
          label="Source package"
          value={loan.sourceIngestionId ? `Ingestion ${loan.sourceIngestionId}` : 'No source package linked'}
        />
        <InfoRow
          icon={ShieldCheck}
          label="Documents on file"
          value={
            // Vault uploads plus the files the deal arrived with, as the
            // dossier's Documents tab counts them.
            (loan.documents?.length ?? 0) + sourceFiles.length > 0
              ? `${(loan.documents?.length ?? 0) + sourceFiles.length} listed`
              : sourcePkg.isLoading
                ? 'Loading…'
                : 'None listed'
          }
        />
        <InfoRow icon={Network} label="Related deals" value={`${related.length} by sponsor/borrower`} />
      </div>

      {loan.sourceIngestionId ? (
        <div className="space-y-1.5">
          <SectionLabel label="Original email & attachments" />
          {sourcePkg.isLoading ? (
            <p className="text-[11px] text-stone-600">Loading source files…</p>
          ) : sourcePkg.isError ? (
            <p className="text-[11px] text-red-300">
              Could not load the linked intake package. Open Intake and re-upload the email to restore it.
            </p>
          ) : sourceFiles.length === 0 ? (
            <p className="text-[11px] text-stone-600">
              This deal is linked to intake {loan.sourceIngestionId}, but no stored source files are on the record.
              Re-upload the original email on Intake to restore them.
            </p>
          ) : (
            <>
              <button
                type="button"
                onClick={() =>
                  setViewerSha(
                    sourceFiles.find((f) => f.kind === 'email_source')?.sha256 ?? sourceFiles[0].sha256,
                  )
                }
                className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-amber-400/30 bg-amber-400/[0.06] px-3 py-2 text-[11px] text-amber-300 hover:bg-amber-400/10"
              >
                <Eye className="h-3.5 w-3.5" />
                Open original email &amp; attachments
              </button>
              <div className="space-y-1">
                {sourceFiles.map((f) => (
                  <button
                    key={f.sha256}
                    type="button"
                    onClick={() => setViewerSha(f.sha256)}
                    className="flex w-full items-center gap-2 rounded border border-white/[0.04] px-2.5 py-1.5 text-left text-[12px] text-stone-300 hover:border-amber-400/30 hover:text-amber-200"
                  >
                    {f.kind === 'email_source' ? (
                      <Mail className="h-3.5 w-3.5 text-blue-400" />
                    ) : (
                      <Paperclip className="h-3.5 w-3.5 text-stone-500" />
                    )}
                    <span className="flex-1 truncate">
                      {f.kind === 'email_source' ? `Original email — ${f.filename}` : f.filename}
                    </span>
                    <Eye className="h-3.5 w-3.5 text-stone-600" />
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      ) : null}

      {related.length > 0 ? (
        <div className="space-y-1.5">
          {related.map((item) => (
            <div key={item.id} className="rounded border border-white/[0.04] px-2.5 py-2 text-[12px] text-stone-300">
              <div className="font-medium text-stone-100">{item.property}</div>
              <div className="text-[10px] text-stone-500">{item.id} · {item.stage}</div>
            </div>
          ))}
        </div>
      ) : null}
      {(loan.documents ?? []).length > 0 ? (
        <div className="space-y-1">
          <SectionLabel label="Documents" />
          {loan.documents.map((doc) => (
            <div key={doc} className="rounded border border-white/[0.04] px-2.5 py-1.5 text-[12px] text-stone-400">
              {doc}
            </div>
          ))}
        </div>
      ) : null}

      {viewerSha && loan.sourceIngestionId && sourceFiles.length > 0 ? (
        <SourceViewerDialog
          ingestionId={loan.sourceIngestionId}
          files={sourceFiles}
          initialSha={viewerSha}
          onClose={() => setViewerSha(null)}
        />
      ) : null}
    </div>
  )
}

function FieldHistoryList({ events }: { events: LoanFieldHistoryEvent[] }) {
  if (events.length === 0) {
    return <div className="mt-2 text-[10px] text-stone-600">No field history events.</div>
  }
  return (
    <div className="mt-2 space-y-1.5 border-t border-white/[0.04] pt-2">
      {events.map((event) => (
        <div key={event.id} className="rounded border border-white/[0.03] bg-black/30 px-2 py-1.5">
          <div className="flex items-center justify-between gap-2">
            <ValueStateBadge state={event.valueState} />
            <div className="font-mono text-[9px] text-stone-600">{event.createdAt || '—'}</div>
          </div>
          <div className="mt-1 font-mono text-[10px] text-stone-400">
            <span className="text-stone-600">{formatFieldValue(event.oldValue)}</span>
            <span className="mx-1 text-amber-200/50">→</span>
            <span className="text-stone-200">{formatFieldValue(event.newValue)}</span>
          </div>
          <div className="mt-0.5 text-[10px] text-stone-500">
            {[event.editorEmail || 'system', event.source, event.reason].filter(Boolean).join(' · ')}
          </div>
          {event.confidence != null ? (
            <div className="mt-0.5 text-[9px] text-stone-600">Confidence {event.confidence}</div>
          ) : null}
        </div>
      ))}
    </div>
  )
}

function LineageFieldCard({
  field,
  loanId,
  expanded,
  onToggle,
  canApprove,
}: {
  field: LoanFieldLineageField
  loanId: string
  expanded: boolean
  onToggle: () => void
  /** POST .../fields/{name}/approve requires pipeline:edit, as the Save does. */
  canApprove: boolean
}) {
  const queryClient = useQueryClient()
  const [approving, setApproving] = useState(false)
  const [reason, setReason] = useState('')
  const [approveError, setApproveError] = useState<string | null>(null)

  const approveMutation = useMutation({
    mutationFn: () => loansService.approveField(loanId, field.fieldName, reason.trim()),
    onSuccess: async () => {
      setApproving(false)
      setReason('')
      setApproveError(null)
      await invalidateLoanInspectorQueries(queryClient, loanId)
    },
    onError: () => {
      setApproveError('Unable to approve field.')
    },
  })

  const isMissing = field.valueState === 'missing'
  // R1: people resolve inputs, not outputs — a derived value is not approved.
  const isDerived = stateOfLineage(field) === 'derived'
  const history = field.history ?? []

  return (
    <div
      className={clsx(
        'rounded-md border px-3 py-2',
        isMissing
          ? 'border-amber-300/15 bg-amber-300/[0.03]'
          : 'border-white/[0.04] bg-white/[0.01]',
      )}
    >
      <button type="button" onClick={onToggle} className="flex w-full items-start justify-between gap-2 text-left">
        <div className="min-w-0 flex items-start gap-1.5">
          {expanded ? (
            <ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-500" />
          ) : (
            <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-500" />
          )}
          <div className="min-w-0">
            <div className="text-[12px] font-medium text-stone-100">{field.fieldName}</div>
            <div
              className={clsx(
                'mt-0.5 font-mono text-[11px]',
                isMissing ? 'text-amber-200/70' : 'text-stone-300',
              )}
            >
              {formatFieldValue(heldValue(field))}
            </div>
          </div>
        </div>
        <ValueStateBadge state={stateOfLineage(field)} />
      </button>

      <div className="mt-1.5 space-y-0.5 pl-5 text-[10px] text-stone-500">
        {field.summary || field.source ? <div>Source: {field.summary ?? field.source}</div> : null}
        {field.confidence != null ? <div>Confidence: {field.confidence}</div> : null}
        {(field.editorEmail || field.updatedAt || field.createdAt) && (
          <div>
            {[field.editorEmail, field.updatedAt || field.createdAt].filter(Boolean).join(' · ')}
          </div>
        )}
        {field.reason ? <div>Reason: {field.reason}</div> : null}
      </div>

      {!approving ? (
        <div className="mt-2 flex pl-5">
          <button
            type="button"
            onClick={() => {
              setApproving(true)
              setApproveError(null)
            }}
            disabled={isMissing || isDerived || !canApprove}
            title={
              !canApprove
                ? NO_DEAL_EDIT
                : isDerived
                  ? 'Derived by the platform — confirm or correct its inputs instead'
                  : undefined
            }
            className="inline-flex items-center gap-1 rounded border border-amber-300/20 px-2 py-1 text-[10px] text-amber-200 hover:bg-amber-300/10 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Check className="h-3 w-3" /> Approve
          </button>
        </div>
      ) : (
        <div className="mt-2 space-y-1.5 pl-5">
          <label className="block">
            <span className="text-[9px] uppercase tracking-[0.14em] text-stone-600">Approval reason</span>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Required reason"
              className="mt-1 w-full rounded border border-stone-700 bg-black/40 px-2 py-1.5 text-[12px] text-stone-100 outline-none focus:border-amber-300/40"
              autoFocus
            />
          </label>
          {approveError ? <div className="text-[10px] text-red-300">{approveError}</div> : null}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!reason.trim() || approveMutation.isPending}
              onClick={() => approveMutation.mutate()}
              className="rounded bg-amber-300 px-2.5 py-1 text-[10px] font-medium text-black disabled:opacity-60"
            >
              {approveMutation.isPending ? 'Saving…' : 'Confirm approve'}
            </button>
            <button
              type="button"
              onClick={() => {
                setApproving(false)
                setReason('')
                setApproveError(null)
              }}
              className="rounded border border-stone-600 px-2.5 py-1 text-[10px] text-stone-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {expanded ? <FieldHistoryList events={history} /> : null}
    </div>
  )
}

function LineageTab({ loan }: { loan: Loan }) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const canEditDeal = useCanEditDeal()

  const query = useQuery({
    queryKey: LOAN_QUERY_KEYS.fieldLineage(loan.id),
    queryFn: () => loansService.getFieldLineage(loan.id),
  })

  useEffect(() => {
    setExpanded({})
  }, [loan.id])

  if (query.isLoading) {
    return <div className="py-8 text-center text-sm text-stone-500">Loading field lineage…</div>
  }
  if (query.isError) {
    return <div className="py-8 text-center text-sm text-red-300">Unable to load field lineage.</div>
  }

  const data = query.data
  if (!data) {
    return (
      <div className="rounded-md border border-stone-700/60 bg-stone-900/40 px-3 py-4 text-[12px] text-stone-400">
        No field lineage available.
      </div>
    )
  }

  const fields = data.fields ?? []

  return (
    <div className="space-y-4">
      {data.linked && data.package ? (
        <>
          <SectionLabel label="Source package" />
          <div className="rounded-md border border-white/[0.04] bg-white/[0.015] px-3 py-2 text-[12px] text-stone-300">
            <div>{data.package.sourceSubject || data.package.sourceType || 'Linked package'}</div>
            <div className="mt-1 text-[10px] text-stone-500">
              {[data.package.sourceEmail, data.package.receivedAt, data.sourceIngestionId]
                .filter(Boolean)
                .join(' · ')}
            </div>
          </div>
        </>
      ) : (
        <div className="rounded-md border border-stone-700/60 bg-stone-900/40 px-3 py-3 text-[12px] text-stone-400">
          {data.message || 'No source package linked'}
        </div>
      )}

      <SectionLabel label="Field lineage" action={fields.length ? `${fields.length} fields` : undefined} />
      {fields.length === 0 ? (
        <div className="rounded-md border border-stone-700/60 px-3 py-3 text-[12px] text-stone-400">
          {data.message || 'No field lineage recorded for this deal.'}
        </div>
      ) : (
        <div className="space-y-2">
          {fields.map((field) => (
            <LineageFieldCard
              key={field.fieldName}
              field={field}
              loanId={loan.id}
              canApprove={canEditDeal}
              expanded={Boolean(expanded[field.fieldName])}
              onToggle={() =>
                setExpanded((current) => ({
                  ...current,
                  [field.fieldName]: !current[field.fieldName],
                }))
              }
            />
          ))}
        </div>
      )}
    </div>
  )
}

function AuditTab({ loan }: { loan: Loan }) {
  const historyQuery = useQuery({
    queryKey: LOAN_QUERY_KEYS.fieldHistory(loan.id),
    queryFn: () => loansService.getFieldHistory(loan.id),
  })

  const activityQuery = useQuery({
    queryKey: LOAN_QUERY_KEYS.activity(loan.id),
    queryFn: () => loansService.getActivity(loan.id),
  })

  if (historyQuery.isLoading) {
    return <div className="py-8 text-center text-sm text-stone-500">Loading field history…</div>
  }

  const events = historyQuery.isError ? [] : (historyQuery.data ?? [])
  const systemEvents = activityQuery.data ?? []

  return (
    <div className="space-y-5">
      <section>
        <SectionLabel
          label="Field history"
          action={historyQuery.isError ? 'Unavailable' : events.length ? `${events.length} events` : undefined}
        />
        {historyQuery.isError ? (
          <div className="mt-2 rounded-md border border-red-400/20 px-3 py-3 text-[12px] text-red-300">
            Unable to load field history.
          </div>
        ) : events.length === 0 ? (
          <div className="mt-2 rounded-md border border-stone-700/60 bg-stone-900/40 px-3 py-4 text-[12px] text-stone-400">
            No field history events recorded for this deal yet.
          </div>
        ) : (
          <div className="mt-2 space-y-2">
            {events.map((event) => (
              <div key={event.id} className="rounded-md border border-white/[0.04] px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[12px] font-medium text-stone-100">{event.fieldName}</div>
                  <ValueStateBadge state={event.valueState} />
                </div>
                <div className="mt-1 font-mono text-[11px] text-stone-300">
                  <span className="text-stone-500">{formatFieldValue(event.oldValue)}</span>
                  <span className="mx-1.5 text-amber-200/60">→</span>
                  <span>{formatFieldValue(event.newValue)}</span>
                </div>
                <div className="mt-1 flex items-center justify-between gap-2 text-[10px] text-stone-500">
                  <span>{event.editorEmail || 'system'}</span>
                  <span className="font-mono">{event.createdAt || '—'}</span>
                </div>
                {event.reason ? (
                  <div className="mt-0.5 text-[10px] text-stone-500">Reason: {event.reason}</div>
                ) : null}
                {event.source ? (
                  <div className="mt-0.5 text-[10px] text-stone-600">Source: {event.source}</div>
                ) : null}
                {event.confidence != null ? (
                  <div className="mt-0.5 text-[9px] text-stone-600">Confidence {event.confidence}</div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <SectionLabel
          label="System events"
          action={
            activityQuery.isLoading
              ? 'Loading…'
              : activityQuery.isError
                ? 'Unavailable'
                : systemEvents.length
                  ? `${systemEvents.length} events`
                  : undefined
          }
        />
        {activityQuery.isError ? (
          <div className="mt-2 rounded-md border border-stone-700/60 px-3 py-3 text-[12px] text-stone-500">
            System activity unavailable.
          </div>
        ) : systemEvents.length === 0 ? (
          <div className="mt-2 rounded-md border border-stone-700/60 px-3 py-3 text-[12px] text-stone-500">
            No system activity events for this deal.
          </div>
        ) : (
          <div className="mt-2 space-y-2">
            {systemEvents.map((event) => (
              <div key={event.id} className="rounded-md border border-white/[0.04] px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[12px] font-medium text-stone-100">{event.eventType}</div>
                  <div className="font-mono text-[10px] text-stone-500">{event.createdAt || '—'}</div>
                </div>
                <div className="mt-1 text-[11px] text-stone-400">{event.userEmail || 'system'}</div>
                {event.details?.fields ? (
                  <div className="mt-1 text-[10px] text-stone-500">
                    Fields:{' '}
                    {Array.isArray(event.details.fields)
                      ? event.details.fields.join(', ')
                      : String(event.details.fields)}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

export function DealInspector({
  loan,
  allLoans,
  tab,
  setTab,
  onClose,
  onSave,
  saving,
  embedded = false,
}: {
  loan: Loan
  allLoans: Loan[]
  tab: InspectorTab
  setTab: (tab: InspectorTab) => void
  onClose: () => void
  onSave: (patch: EditableLoanPatch) => Promise<void>
  saving: boolean
  /** Rendered inside the deal workspace rather than docked beside the grid:
   *  fills its container and drops the close affordance, which the workspace
   *  owns. Without this the panel keeps its 420px cap and its own X, giving
   *  the modal two close buttons that do different things. */
  embedded?: boolean
}) {
  const exceptions = loanExceptions(loan)
  const [logKind, setLogKind] = useState<InspectorLogKind | null>(null)

  useEffect(() => {
    setLogKind(null)
  }, [loan.id])

  const tabs: Array<{ id: InspectorTab; label: string }> = [
    { id: 'overview', label: 'Overview' },
    { id: 'economics', label: 'Economics' },
    { id: 'blockers', label: 'Blockers' },
    { id: 'activity', label: 'Activity' },
    { id: 'connections', label: 'Connections' },
    { id: 'lineage', label: 'Lineage' },
    { id: 'audit', label: 'Audit' },
  ]

  const openLog = (kind: InspectorLogKind) => {
    setLogKind(kind)
  }

  return (
    <aside
      className={clsx(
        'flex h-full w-full flex-col bg-[#0a0a0a]',
        embedded ? 'min-h-0' : 'max-w-[360px] border-l border-amber-300/10',
      )}
    >
      <div className="flex items-start justify-between gap-2 border-b border-amber-300/10 px-3 py-2">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-amber-200/70">
            <PanelRightOpen className="h-3.5 w-3.5" /> Deal inspector
          </div>
          <div className="mt-1 text-sm font-medium text-stone-100">{loan.property || loan.id}</div>
          <div className="mt-0.5 font-mono text-[10px] text-stone-500">{loan.id}</div>
        </div>
        {!embedded && (
          <button type="button" onClick={onClose} className="rounded p-1 text-stone-500 hover:bg-white/5 hover:text-stone-200">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-amber-300/10 px-2 py-1">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => {
              setLogKind(null)
              setTab(item.id)
            }}
            className={clsx(
              'shrink-0 rounded px-2 py-0.5 text-[10px]',
              !logKind && tab === item.id
                ? 'bg-amber-300/15 text-amber-100'
                : 'text-stone-500 hover:text-stone-300',
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-2.5">
        {logKind ? (
          <InspectorLogCompose
            loan={loan}
            kind={logKind}
            onClose={() => setLogKind(null)}
            onSaved={() => {
              setLogKind(null)
              setTab('activity')
            }}
          />
        ) : (
          <>
            {tab === 'overview' ? (
              <OverviewTab loan={loan} exceptions={exceptions} onSave={onSave} saving={saving} />
            ) : null}
            {tab === 'economics' ? <InspectorEconomicsTab loan={loan} /> : null}
            {tab === 'blockers' ? <BlockersTab loan={loan} /> : null}
            {tab === 'activity' ? (
              <ActivitySummaryTab loan={loan} onViewAll={() => setTab('audit')} />
            ) : null}
            {tab === 'connections' ? <InspectorConnections loan={loan} allLoans={allLoans} /> : null}
            {tab === 'lineage' ? <LineageTab loan={loan} /> : null}
            {tab === 'audit' ? <AuditTab loan={loan} /> : null}
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-t border-amber-300/10 p-3">
        {!embedded && (
          <button
            type="button"
            onClick={() => useDealDossierStore.getState().openDossier(loan.id, 'overview', loan)}
            className="rounded border border-amber-300/20 px-3 py-1.5 text-[11px] text-amber-100 hover:bg-amber-300/10"
          >
            Open deal dossier
          </button>
        )}
        <KillDealPanel loan={loan} />
      </div>

      <InspectorLogActionRow active={logKind} onSelect={openLog} />
    </aside>
  )
}
